import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, Copy, Loader2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, fmtINR } from "@/lib/api";

const Approve = ({ a, auth, onDone }) => {
  const [pct, setPct] = useState(25);
  const [busy, setBusy] = useState(false);
  const act = async (kind) => {
    setBusy(true);
    try {
      const { data } = kind === "approve" ? await api.post(`/admin/school-applications/${a.id}/approve`, { discount_pct: Number(pct) }, auth)
        : await api.post(`/admin/school-applications/${a.id}/reject`, {}, auth);
      toast.success(kind === "approve" ? `Approved · ${fmtINR(data.total_inr)} bill emailed` : "Declined");
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail?.[0]?.msg || e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="flex items-center gap-2">
      <Input type="number" min="20" max="30" value={pct} onChange={(e) => setPct(e.target.value)} className="w-16 h-8" data-testid={`school-discount-${a.id}`} /><span className="text-xs">%</span>
      <Button size="sm" disabled={busy} onClick={() => act("approve")} className="h-8 bg-[#0A192F] text-white" data-testid={`school-approve-${a.id}`}>{busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}</Button>
      <Button size="sm" variant="outline" disabled={busy} onClick={() => act("reject")} className="h-8" data-testid={`school-reject-${a.id}`}><X className="w-3 h-3" /></Button>
    </div>
  );
};

const InvoiceCell = ({ inv }) => {
  const link = `${window.location.origin}/school-billing/${inv.token}`;
  return (
    <div className="text-xs whitespace-nowrap">
      <b>{fmtINR(inv.total_inr)}</b> ({inv.discount_pct}% off) · <span className={inv.payment_status === "paid" ? "text-green-700 font-bold" : "text-amber-700 font-bold"} data-testid={`school-invoice-status-${inv.id}`}>{inv.payment_status}</span>
      <button onClick={() => { navigator.clipboard?.writeText(link); toast.success("Pay link copied"); }} className="ml-2 inline-flex items-center gap-1 text-[#007A8A]" data-testid={`school-invoice-copy-${inv.id}`}><Copy className="w-3 h-3" />link</button>
    </div>
  );
};

export const SchoolOnboardingAdmin = ({ auth, onErr }) => {
  const [items, setItems] = useState(null);
  const load = useCallback(() => api.get("/admin/school-applications", auth).then((r) => setItems(r.data.items)).catch(onErr), [auth, onErr]);
  useEffect(() => { load(); }, [load]);
  if (!items) return <Loader2 className="w-6 h-6 animate-spin mt-8" />;
  return (
    <div className="mt-6 bg-white border border-slate-200 rounded-2xl overflow-x-auto" data-testid="school-onboarding-admin">
      <table className="w-full text-sm">
        <thead className="bg-[#F0F4F8] text-left text-xs uppercase tracking-wider text-slate-500"><tr>{["Applied", "School", "Contact", "Area / billing", "Base fee", "Roster", "Status", "Action / bill"].map((h) => <th key={h} className="px-3 py-2">{h}</th>)}</tr></thead>
        <tbody>{items.map((a) => (
          <tr key={a.id} className="border-t border-slate-100" data-testid={`school-app-row-${a.id}`}>
            <td className="px-3 py-2 whitespace-nowrap">{new Date(a.created_at).toLocaleDateString("en-IN")}</td>
            <td className="px-3 py-2 font-bold">{a.school_name}<div className="text-xs font-normal text-slate-500">{a.city}, {a.state}</div></td>
            <td className="px-3 py-2">{a.contact_person}<div className="text-xs text-slate-500">{a.email} · {a.phone}</div></td>
            <td className="px-3 py-2 capitalize">{a.area} · {a.billing}</td><td className="px-3 py-2">{fmtINR(a.base_fee_inr)}</td>
            <td className="px-3 py-2">{a.roster_count}</td><td className="px-3 py-2 font-bold capitalize">{a.status}</td>
            <td className="px-3 py-2">{a.status === "pending" ? <Approve a={a} auth={auth} onDone={load} /> : a.invoice ? <InvoiceCell inv={a.invoice} /> : "—"}</td>
          </tr>))}
          {!items.length && <tr><td colSpan={8} className="px-3 py-8 text-center text-slate-500">No onboarding requests yet</td></tr>}
        </tbody>
      </table>
    </div>
  );
};
